import { useEffect, useRef, useState } from 'react';
import { Box, FormControl, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import { identityApiRef } from '@backstage/core-plugin-api';

export const UserField = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
}: FieldExtensionComponentProps<any>) => {
  const identityApi = useApi(identityApiRef);
  const config = useApi(configApiRef);
  const SYS_INFO_BACKEND_URL = 'backend.baseUrl';
  const backendUrl = config.getString(SYS_INFO_BACKEND_URL);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const label: string = String(uiSchema['ui:options']?.label || 'User');

  const [users, setUsers] = useState<any[]>([]);
  const [userRef, setUserRef] = useState('');

  // -----------------------------------------------------------------------------------
  // ✔️ 1. Sincronizar con formData
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    if (!formData) return;
    console.log(formData);
    setUserRef(formData || '');
  }, [formData]);

  // -----------------------------------------------------------------------------------
  // ✔️ 2. Cargar proyectos donde el usuario es JP
  // -----------------------------------------------------------------------------------
  // useEffect(() => {
  //   const fetchUsers = async () => {
  //     const resp = await catalogApi.getEntities({
  //       filter: { kind: 'User' },
  //     });

  //     setUsers(resp.items);
  //   };

  //   fetchUsers();
  // }, [identityApi, catalogApi]);

  const fetchCatalogUsersPage = async (query: string, signal?: AbortSignal) => {
    try {
      const token = (await identityApi.getCredentials()).token;
      const url = query.trim() === ''
        ? `${backendUrl}/api/catalog/entities/by-query?limit=50&offset=0&orderField=metadata.name%2Casc&filter=kind%3Duser&fullTextFilterFields=metadata.name,metadata.title,spec.profile.displayName,spec.profile.email`
        : `${backendUrl}/api/catalog/entities/by-query?limit=50&offset=0&orderField=metadata.name%2Casc&filter=kind%3Duser&fullTextFilterTerm=${query}&fullTextFilterFields=metadata.name,metadata.title,spec.profile.displayName,spec.profile.email`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        signal,
      });

      const items: any = await response.json();

      return items.items;
    } finally {
    }
  };

  const handleSearchInput = (query: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();

    if (query.trim().length < 3) {
      setUsers([]); // limpia buffer si < 3 letras
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const items = await fetchCatalogUsersPage(query, controller.signal);
        setUsers(items);  // solo reemplaza buffer, MUI mantiene foco
      } catch (e: any) {
        if (e.name === 'AbortError') return;
        console.error('[PTM] fetchCatalogUsersPage error', e);
        setUsers([]);
      }
    }, 300);
  };

  // -----------------------------------------------------------------------------------
  // ✔️ Render
  // -----------------------------------------------------------------------------------
  return (
    <FormControl margin="normal" required={required} fullWidth>
      <Autocomplete
        options={users}
        getOptionLabel={opt => opt?.spec?.profile?.displayName || ''}
        filterOptions={(x) => x}      // desactiva filtro interno
        onInputChange={(_, value, reason) => {
          if (reason !== 'input') return;
          handleSearchInput(value);   // debounce + mínimo 3 letras
        }}
        clearOnBlur={false}          // no borrar texto al perder foco
        blurOnSelect={false}         // no perder foco al seleccionar
        openOnFocus                  // mantiene dropdown abierto
        value={
          users.find(
            p => `user:${p.metadata?.namespace}/${p.metadata?.name}` === userRef,
          ) || null
        }
        onChange={(_, newValue) => {
          if (newValue) onChange(`user:${newValue.metadata?.namespace}/${newValue.metadata?.name}`);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            required
            helperText={rawErrors?.[0] || `Select the ${label.toLowerCase()}`}
          />
        )}
        renderOption={(option) => (
          <Box>
            <Typography variant="body2">{option?.spec?.profile?.displayName}</Typography>
            <Typography variant="caption" color="textSecondary">
              {option?.spec?.profile?.email || ''}
            </Typography>
          </Box>
        )}
      />
    </FormControl>
  );
};

// -----------------------------------------------------------------------------------
// ✔️ Validación
// -----------------------------------------------------------------------------------
export const userCaseValidation = (
  value: string,
  validation: FieldValidation,
) => {
  if (!value) validation.addError('User is required.');
};

// -----------------------------------------------------------------------------------
// ✔️ Registro del Custom Field
// -----------------------------------------------------------------------------------
export const UserCaseFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'UserField',
    component: UserField,
    validation: userCaseValidation,
  }),
);
